import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { apiClient } from "../../../../../api/httpClient";
import { uploadGIA, uploadLOA } from "../../../../../api/bondsClient";
import {
  buildLawFirmFuzzyDialogResult,
  notifyExactLawFirmDuplicate,
  requestLawFirmFuzzyMatch,
} from "../../../../../features/bonds/shared/utils/lawFirmFuzzyMatch";
import { normalizeUsState, isAllowedUsState } from "../../../../../globals/utils/usStates";

function useCreateLawFirm() {
  const navigate = useNavigate();

  // STEPS
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Law Firm Details", "Add Locations", "Add Users"];

  // LOADING & ID
  const [loading, setLoading] = useState(false);
  const [lawfirmid, setLawfirmid] = useState(null);

  // ------------------ STEP 0: LAW FIRM INFO ------------------
  const [lawFirmData, setLawFirmData] = useState({
    name: "",
    website: "",
    logo: null,
    accountingContactName: "",
    accountingContactEmail: "",
    accountingContactPhone: "",
    logoName: "",
    managed_by: null,
  });
  const [fuzzyMatchDialogOpen, setFuzzyMatchDialogOpen] = useState(false);
  const [fuzzyMatchResult, setFuzzyMatchResult] = useState(null);
  const [usedFuzzyMatchedLawFirm, setUsedFuzzyMatchedLawFirm] = useState(false);
  const [fuzzyMatchLocationDialogOpen, setFuzzyMatchLocationDialogOpen] = useState(false);
  const [fuzzyMatchLocationResult, setFuzzyMatchLocationResult] = useState(null);
  const [pendingLocationSubmitData, setPendingLocationSubmitData] = useState(null);

  const handleLawFirmChange = (e) => {
    const { name, value } = e.target;
    setLawFirmData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Optionally truncate the filename
      const truncatedFileName = file.name.slice(0, 50);
      setLawFirmData((prev) => ({
        ...prev,
        logo: file,
        logoName: truncatedFileName,
      }));
    }
  };

  const doCreateLawFirm = async () => {
    const formData = new FormData();
    if (lawFirmData.logo) formData.append("logo", lawFirmData.logo);
    formData.append("name", lawFirmData.name);
    formData.append("website", lawFirmData.website);
    formData.append(
      "accounting_contact_name",
      lawFirmData.accountingContactName
    );
    formData.append(
      "accounting_contact_email",
      lawFirmData.accountingContactEmail
    );
    formData.append(
      "accounting_contact_phone",
      lawFirmData.accountingContactPhone
    );
    if (lawFirmData.managed_by !== null) {
      formData.append("managed_by", lawFirmData.managed_by);
    }
    formData.append("is_new", true);
    formData.append("is_active", true);
    formData.append("created_by_guest", false);
    const response = await apiClient(
      "/create-a-lawfirm",
      "POST",
      formData,
      {},
      { "content-type": "multipart/form-data" }
    );
    setLawfirmid(response.lawfirm_id);
    setUsedFuzzyMatchedLawFirm(false);
    toast.success("Law Firm created successfully!");
    setActiveStep(1);
  };

  const handleCreateLawFirm = async () => {
    try {
      if (!lawFirmData.name) {
        toast.error("Please fill all the required fields");
        return;
      }
      setLoading(true);
      const parseResult = await requestLawFirmFuzzyMatch(apiClient, lawFirmData.name);
      const dialogResult = buildLawFirmFuzzyDialogResult(parseResult, lawFirmData.name);
      if (dialogResult) {
        setLoading(false);
        setFuzzyMatchResult(dialogResult);
        setFuzzyMatchDialogOpen(true);
        return;
      }
      await doCreateLawFirm();
    } catch (error) {
      console.error("Error creating law firm:", error);
      toast.error("Failed to create Law Firm. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUseExistingLawFirm = () => {
    if (!fuzzyMatchResult) return;
    setLawfirmid(fuzzyMatchResult.id);
    setUsedFuzzyMatchedLawFirm(true);
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    toast.success("Using existing law firm.");
    setActiveStep(1);
  };

  const handleCreateNewLawFirmAnyway = async () => {
    if (notifyExactLawFirmDuplicate(fuzzyMatchResult?.exactMatch, toast)) {
      return;
    }
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    try {
      setLoading(true);
      await doCreateLawFirm();
    } catch (error) {
      console.error("Error creating law firm:", error);
      toast.error("Failed to create Law Firm. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // --- Internal Users for Managed by ---
  const [internalUsers, setInternalUsers] = useState([]);

  const fetchInternalUsers = async () => {
    try {
      const internalUsers = await apiClient("/internal-user", "GET");
      setInternalUsers(internalUsers.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    void fetchInternalUsers();
  }, []);

  // ------------------ STEP 1: ADD LOCATIONS ------------------
  const [locations, setLocations] = useState([
    {
      name: "Main Location",
      address: "",
      zipCode: "",
      city: "",
      county: "",
      state: "",
      phone: "",
      fax: "",
      customerNumber: "",
      useSeparateAccounting: false,
      accountingContact: { name: "", email: "", phone: "" },
      addressTypes: { physical: false, mailing: false, billing: false },
    },
  ]);

  useEffect(() => {
    console.log(locations);
  }, [locations]);

  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [allCounties, setAllCounties] = useState([]);
  const [lawFirmLocations, setLawFirmLocations] = useState([]); // For Step 2 usage (fetched after creation)
  const [addressFieldErrors, setAddressFieldErrors] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        const counties = await apiClient("/fields/counties/", "GET");
        setAllCities(cities);
        setAllStates(states);
        setAllCounties(counties);
      } catch (error) {
        console.error("Error loading cities or states:", error);
      }
    };
    void loadData();
  }, []);

  const handleLocationChange = (index, field, value) => {
    const updatedLocations = [...locations];
    updatedLocations[index][field] = value;
    setLocations(updatedLocations);
    setAddressFieldErrors((prev) => {
      const next = prev.slice();
      while (next.length <= index) next.push({});
      next[index] = { ...next[index], [field]: false };
      return next;
    });
  };

  const addLocation = () => {
    setLocations((prev) => [
      ...prev,
      {
        name: `Location ${prev.length + 1}`,
        address: "",
        zipCode: "",
        city: "",
        country: "USA",
        county: "",
        state: "",
        phone: "",
        fax: "",
        customerNumber: "",
        useSeparateAccounting: false,
        accountingContact: { name: "", email: "", phone: "" },
        addressTypes: { physical: false, mailing: false, billing: false },
      },
    ]);
  };

  const removeLocation = (index) => {
    setLocations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGoogleCallback = (
    index,
    address,
    city,
    state,
    county,
    zipCode
  ) => {
    handleLocationChange(index, "address", address);
    handleLocationChange(index, "city", city);
    handleLocationChange(index, "county", county);
    handleLocationChange(index, "state", normalizeUsState(state, allStates));
    handleLocationChange(index, "zipCode", zipCode);
  };

  const handleZipCodeChange = async (index, zipCode) => {
    handleLocationChange(index, "zipCode", zipCode);
    if (zipCode.length === 5) {
      try {
        const response = await apiClient(`/fields/location/${zipCode}`, "GET");
        const { city, state } = response;
        handleLocationChange(index, "city", city);
        handleLocationChange(index, "state", normalizeUsState(state, allStates));
      } catch (error) {
        console.error("Error fetching city/state by zip code:", error);
      }
    }
  };

  const handleCreateLocation = async () => {
    const errors = locations.map((loc) => ({
      address: !(loc.address && String(loc.address).trim()),
      zipCode: !(loc.zipCode && String(loc.zipCode).trim()),
      city: !(loc.city && String(loc.city).trim()),
      state: !(loc.state && String(loc.state).trim()),
      county: !(loc.county && String(loc.county).trim()),
    }));
    const hasErrors = errors.some((e) => e.address || e.zipCode || e.city || e.state || e.county);
    if (hasErrors) {
      setAddressFieldErrors(errors);
      toast.error("Please fill in all required address fields: Address, Zip Code, City, State, and County.");
      return;
    }
    setAddressFieldErrors([]);

    // Create location(s) for the newly created lawfirm
    const normalizedLocations = locations.map((location) => {
      const normalizedState = normalizeUsState(location.state, allStates);
      return { ...location, state: normalizedState };
    });

    const invalidState = normalizedLocations.find(
      (loc) => loc.state && !isAllowedUsState(loc.state, allStates)
    );
    if (invalidState) {
      toast.error(`Invalid state "${invalidState.state}". Please select a valid state.`);
      return;
    }

    const dataToSend = normalizedLocations.map((location) => ({
      lawfirm_id: lawfirmid,
      name: location.name,
      address: location.address,
      zip_code: location.zipCode,
      city: location.city,
      county: location.county,
      country: "USA",
      state: location.state,
      phone: location.phone,
      fax: location.fax,
      customer_number: location.customerNumber,
      use_separate_firm_accounting_contact: location.useSeparateAccounting,
      accounting_contact_full_name: location.accountingContact.name,
      accounting_contact_email: location.accountingContact.email,
      accounting_contact_phone: location.accountingContact.phone,
      is_physical_address: location.addressTypes.physical,
      is_mailing_address: location.addressTypes.mailing,
      is_billing_address: location.addressTypes.billing,
      is_active: true,
    }));

    // When user chose "Use existing" law firm (fuzzy match), check each location for duplicate
    if (usedFuzzyMatchedLawFirm) {
      for (let i = 0; i < dataToSend.length; i++) {
        const loc = dataToSend[i];
        try {
          const fuzzyRes = await apiClient("/fuzzy-match-lawfirm-location", "POST", {
            lawfirm_id: Number(lawfirmid),
            address: loc.address,
            city: loc.city,
            state: loc.state,
            zip_code: loc.zip_code,
            county: loc.county || "",
            country: "USA",
          });
          const matchedId = fuzzyRes?.match?.law_firm_location_id;
          if (fuzzyRes?.match != null && matchedId != null) {
            setPendingLocationSubmitData(dataToSend);
            setFuzzyMatchLocationResult({
              locationIndex: i,
              law_firm_location_id: matchedId,
              score: fuzzyRes?.score ?? 0,
              addressSummary: [loc.address, loc.city, loc.state, loc.zip_code].filter(Boolean).join(", "),
            });
            setFuzzyMatchLocationDialogOpen(true);
            return;
          }
        } catch (err) {
          console.error("Fuzzy match check failed for location:", err);
        }
      }
    }

    try {
      setLoading(true);
      await apiClient(`/lawfirm-location/`, "POST", dataToSend);

      // After creating locations, fetch them back so step 3 can reference them
      const newlyCreatedLocations = await apiClient(
        `/get-locations-by-lawfirm/${lawfirmid}`,
        "GET"
      );
      setLawFirmLocations(newlyCreatedLocations);
      toast.success("Locations created successfully!");
      setActiveStep(2); // Move to step 3 (Add Users)
    } catch (error) {
      console.error("Error creating locations:", error);
      toast.error("Failed to create locations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUseExistingLocation = async () => {
    if (!pendingLocationSubmitData || fuzzyMatchLocationResult == null) return;
    const index = fuzzyMatchLocationResult.locationIndex;
    const filtered = pendingLocationSubmitData.filter((_, i) => i !== index);
    setFuzzyMatchLocationDialogOpen(false);
    setFuzzyMatchLocationResult(null);
    setPendingLocationSubmitData(null);
    try {
      setLoading(true);
      if (filtered.length > 0) {
        await apiClient(`/lawfirm-location/`, "POST", filtered);
      }
      const newlyCreatedLocations = await apiClient(
        `/get-locations-by-lawfirm/${lawfirmid}`,
        "GET"
      );
      setLawFirmLocations(newlyCreatedLocations);
      toast.success(filtered.length > 0 ? "Locations created successfully!" : "Skipped creating duplicate location.");
      setActiveStep(2);
    } catch (error) {
      console.error("Error creating locations:", error);
      toast.error("Failed to create locations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewLocationAnyway = async () => {
    if (!pendingLocationSubmitData) return;
    const dataToSend = pendingLocationSubmitData;
    setFuzzyMatchLocationDialogOpen(false);
    setFuzzyMatchLocationResult(null);
    setPendingLocationSubmitData(null);
    try {
      setLoading(true);
      await apiClient(`/lawfirm-location/`, "POST", dataToSend);
      const newlyCreatedLocations = await apiClient(
        `/get-locations-by-lawfirm/${lawfirmid}`,
        "GET"
      );
      setLawFirmLocations(newlyCreatedLocations);
      toast.success("Locations created successfully!");
      setActiveStep(2);
    } catch (error) {
      console.error("Error creating locations:", error);
      toast.error("Failed to create locations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ------------------ STEP 2: ADD USERS ------------------

  const positionOptions = ["Paralegal", "Attorney", "Clerical Staff"];

  const [lawSchools, setLawSchools] = useState([]);
  const [legalServiceProfessionals, setLegalServiceProfessionals] = useState(
    []
  );
  // We'll fetch these once we reach step 2
  useEffect(() => {
    const fetchStep2Data = async () => {
      if (activeStep === 2) {
        try {
          setLoading(true);
          const [lawSchoolsResponse, professionalsResponse] = await Promise.all(
            [
              apiClient("/lawschools/", "GET"),
              apiClient("/legal-service-paralegals/", "GET"),
            ]
          );
          setLawSchools(lawSchoolsResponse.data);
          setLegalServiceProfessionals(
            professionalsResponse.map((pro) => ({
              id: pro.lawfirm_user_id,
              name: `${pro.prefix} ${pro.preferred_name}`,
            }))
          );
        } catch (error) {
          console.error("Error loading data for user creation:", error);
        } finally {
          setLoading(false);
        }
      }
    };
    void fetchStep2Data();
  }, [activeStep]);

  // Each user has separate location
  const [users, setUsers] = useState([
    {
      prefix: "",
      firstName: "",
      lastName: "",
      middleName: "",
      suffix: "",
      email: "",
      useFirmEmail: false,
      firmEmail: "",
      phoneNumber: "",
      extension: "",
      position: "",
      permissionLevel: "User",
      isLegalService: false,
      isDefault: false,
      lawschool: "",
      barNumber: "",
      selectedProfessional: "",
      showCustomForm: false,
      is_gia_on_file: false,
      gia_document_link: "",
      gia_document_name: "",
      gia_file: null,
      is_loa_on_file: false,
      loa_document_link: "",
      loa_document_name: "",
      loa_file: null,
      location: "",
      sendInvite: false,
    },
  ]);

  const handleUserInputChange = (index, field, value) => {
    const updatedUsers = [...users];
    updatedUsers[index] = { ...updatedUsers[index], [field]: value };

    // Reset dependent fields if the position changes
    if (field === "position") {
      updatedUsers[index].isLegalService = false;
      updatedUsers[index].selectedProfessional = "";
      updatedUsers[index].showCustomForm = false;
    }

    setUsers(updatedUsers);
  };

  const handleAddUser = () => {
    setUsers((prev) => [
      ...prev,
      {
        prefix: "",
        firstName: "",
        lastName: "",
        middleName: "",
        suffix: "",
        email: "",
        useFirmEmail: false,
        firmEmail: "",
        phoneNumber: "",
        extension: "",
        position: "",
        permissionLevel: "User",
        isLegalService: false,
        isDefault: false,
        lawschool: "",
        barNumber: "",
        selectedProfessional: "",
        is_gia_on_file: false,
        gia_document_link: "",
        gia_document_name: "",
        gia_file: null,
        showCustomForm: false,
        location: "",
        sendInvite: false,
      },
    ]);
  };

  const handleRemoveUser = (index) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  // Build a preferred name for the user
  const buildPreferredName = (user) => {
    return [
      user.prefix,
      user.firstName,
      user.middleName,
      user.lastName,
      user.suffix,
    ]
      .filter(Boolean)
      .join(" ");
  };

  const handleSubmitAllUsers = async () => {
    setLoading(true);
    try {
      const submissions = users.map(async (user) => {
        // Handle GIA upload first if needed
        let gia_document_link = user.gia_document_link;
        let gia_document_name = user.gia_document_name;
        let loa_document_link = user.loa_document_link;
        let loa_document_name = user.loa_document_name;

        if (
          user.position === "Attorney" &&
          user.gia_file &&
          user.is_gia_on_file
        ) {
          const uploadResponse = await uploadGIA(user.gia_file);
          gia_document_link = uploadResponse.file_url;
          gia_document_name = uploadResponse.file_name;
        }

        if (
          user.position === "Attorney" &&
          user.loa_file &&
          user.is_loa_on_file
        ) {
          const uploadResponse = await uploadLOA(user.loa_file);
          loa_document_link = uploadResponse.file_url;
          loa_document_name = uploadResponse.file_name;
        }

        // If user is an existing paralegal from the list
        if (user.selectedProfessional) {
          return apiClient("/create-lawfirm-location-users", "POST", {
            lawfirm_id: lawfirmid,
            law_firm_location_id: user.location,
            lawfirm_user_id: user.selectedProfessional,
          });
        }

        // Otherwise, invite a brand new user
        return apiClient("/invite-lawfirm-user", "POST", {
          user: {
            email: user.email,
            first_name: user.firstName,
            last_name: user.lastName,
          },
          law_firm_location_id: user.location,
          lawfirm_id: lawfirmid,
          title: user.position,
          position: user.position,
          middle_name: user.middleName,
          phone_number: user.phoneNumber,
          suffix: user.suffix,
          prefix: user.prefix,
          preferred_name: buildPreferredName(user),
          extension: user.extension,
          bar: user.barNumber,
          is_default: user.isDefault,
          is_legal_service: user.isLegalService,
          is_gia_on_file: user.is_gia_on_file,
          gia_document_link: gia_document_link,
          gia_document_name: gia_document_name,
          is_loa_on_file: user.is_loa_on_file,
          loa_document_link,
          loa_document_name,
          is_active: true,
          is_admin: user.permissionLevel === "Admin",
          lawschool: user.lawschool,
          send_invite: user.sendInvite,
          firm_email: user.useFirmEmail ? user.firmEmail : null,
        });
      });

      await Promise.all(submissions);
      const anyInvited = users.some((u) => u.sendInvite);
      toast.success(
        anyInvited
          ? "Users created and invites sent successfully!"
          : "Users created successfully!"
      );
      setLoading(false);

      // After success, you may reset or navigate
      navigate(`/lawfirmdetails/${lawfirmid}`);
      // or setActiveStep(3) if there's a final summary step
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Error submitting form");
    } finally {
      setLoading(false);
    }
  };

  return {
    activeStep,
    steps,
    loading,
    lawFirmData,
    handleLawFirmChange,
    handleFileChange,
    handleCreateLawFirm,
    fuzzyMatchDialogOpen,
    setFuzzyMatchDialogOpen,
    fuzzyMatchResult,
    setFuzzyMatchResult,
    handleUseExistingLawFirm,
    handleCreateNewLawFirmAnyway,
    fuzzyMatchLocationDialogOpen,
    fuzzyMatchLocationResult,
    setFuzzyMatchLocationDialogOpen,
    setFuzzyMatchLocationResult,
    setPendingLocationSubmitData,
    handleUseExistingLocation,
    handleCreateNewLocationAnyway,
    locations,
    addressFieldErrors,
    handleLocationChange,
    handleGoogleCallback,
    handleZipCodeChange,
    allCities,
    allStates,
    allCounties,
    removeLocation,
    addLocation,
    handleCreateLocation,
    users,
    handleRemoveUser,
    handleUserInputChange,
    lawFirmLocations,
    positionOptions,
    lawSchools,
    legalServiceProfessionals,
    handleAddUser,
    handleSubmitAllUsers,
    setUsers,
    internalUsers,
  };
}

export default useCreateLawFirm;
